/**
 * Logger utility for standardized logging across the application.
 * Integrates with Sentry for error tracking in production.
 */

import { captureError, captureMessage, addBreadcrumb } from './sentry';

const isDev = import.meta.env.DEV;

export const logger = {
  debug: (...args: unknown[]) => {
    if (isDev) {
      console.log('[DEBUG]', ...args);
    }
  },
  
  info: (...args: unknown[]) => {
    console.info('[INFO]', ...args);
  },
  
  warn: (message: string, context?: Record<string, unknown>) => {
    console.warn('[WARN]', message, context);
    if (!isDev) {
      addBreadcrumb(message, context);
    }
  },
  
  error: (message: string, error?: Error | unknown, context?: Record<string, unknown>) => {
    console.error('[ERROR]', message, error);
    if (!isDev) {
      if (error instanceof Error) {
        captureError(error, { message, ...context });
      } else {
        captureMessage(message, 'error');
      }
    }
  },
  
  /**
   * Critical errors that require immediate attention.
   * Always sent to Sentry regardless of environment.
   */
  critical: (message: string, error?: Error | unknown, context?: Record<string, unknown>) => {
    console.error('[CRITICAL]', message, error);
    if (error instanceof Error) {
      captureError(error, { level: 'fatal', message, ...context });
    } else {
      captureMessage(`[CRITICAL] ${message}`, 'fatal');
    }
  },
};
